import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';

import { ScreenEvent } from '@confluence/analytics/entry-points/ScreenEvent.ts';

import { SpaceSelection } from '../page-components/SpaceSelectionRadioButtons.tsx';

const i18n = defineMessages({
	addConfirmationDialogTitle: {
		id: 'bulk-permissions.space.add.confirmation-dialog',
		defaultMessage: 'Add {principalName} to all spaces?',
		description:
			'Confirmation dialog title text with a user or group name injected into the message',
	},
	addAllExcludingPersonalConfirmationDialogDescription: {
		id: 'bulk-permissions.space.add-exclude-personal.confirmation-dialog-description',
		defaultMessage:
			'This will give them access to <b>all spaces (excluding personal)</b> on your site as <b>{roleName}</b>.',
		description:
			'The text is shown as body text in the confirmation dialog when the admin is adding a principal to all spaces excluding personal spaces. The {roleName} placeholder is substituted with the selected role name (e.g., "Editor"). Bold formatting is applied via the {b} placeholder.',
	},
	addAllConfirmationDialogDescription: {
		id: 'bulk-permissions.space.add-all.confirmation-dialog-description',
		defaultMessage:
			'This will give them access to <b>all spaces</b> on your site as <b>{roleName}</b>.',
		description:
			'The text is shown as body text in the confirmation dialog when the admin is adding a principal to all spaces including personal spaces. The {roleName} placeholder is substituted with the selected role name. Bold formatting is applied via the {b} placeholder.',
	},
	addAllPersonalConfirmationDialogDescription: {
		id: 'bulk-permissions.space.add-personal.confirmation-dialog-description',
		defaultMessage:
			'This will give them access to <b>all personal spaces</b> on your site as <b>{roleName}</b>.',
		description:
			'The text is shown as body text in the confirmation dialog when the admin is adding a principal to personal spaces only. The {roleName} placeholder is substituted with the selected role name. Bold formatting is applied via the {b} placeholder.',
	},
	confirmationDialogWarning: {
		id: 'bulk-permissions.space.confirmation-dialog-warning',
		defaultMessage: '<b>This can’t be undone.</b>',
		description: 'Confirmation dialog warning text',
	},
	removeConfirmationDialogTitle: {
		id: 'bulk-permissions.space.remove.confirmation-dialog',
		defaultMessage: 'Remove {principalName} from all spaces?',
		description:
			'The text is shown as the title of the confirmation dialog when the admin is removing a principal from all spaces. The {principalName} placeholder is substituted with the name of the user or group being removed.',
	},
	removeAllExcludingPersonalConfirmationDialogDescription: {
		id: 'bulk-permissions.space.remove-exclude-personal.confirmation-dialog-description',
		defaultMessage:
			'This will remove them from <b>all spaces</b> (excluding personal) on your site.',
		description:
			'The text is shown as body text in the confirmation dialog when the admin is removing a principal from all spaces excluding personal spaces. Bold formatting is applied via the {b} placeholder.',
	},
	removeAllConfirmationDialogDescription: {
		id: 'bulk-permissions.space.remove-all.confirmation-dialog-description',
		defaultMessage: 'This will remove them from <b>all spaces</b> on your site.',
		description:
			'The text is shown as body text in the confirmation dialog when the admin is removing a principal from all spaces including personal spaces. Bold formatting is applied via the {b} placeholder.',
	},
	removeAllPersonalConfirmationDialogDescription: {
		id: 'bulk-permissions.space.remove-personal.confirmation-dialog-description',
		defaultMessage: 'This will remove them from <b>all personal spaces</b> on your site.',
		description:
			'The text is shown as body text in the confirmation dialog when the admin is removing a principal from personal spaces only. Bold formatting is applied via the {b} placeholder.',
	},
	cancelButtonText: {
		id: 'bulk-permissions.space.cancel-button-text',
		defaultMessage: 'Cancel',
		description: 'Text label for the button that cancels the bulk permission change operation',
	},
	addButtonText: {
		id: 'bulk-permissions.space.add-button-text',
		defaultMessage: 'Add to all',
		description:
			'The text is shown on the primary confirm button in the bulk access confirmation dialog when the admin is adding a principal to all spaces.',
	},
	removeButtonText: {
		id: 'bulk-permissions.space.remove-button-text',
		defaultMessage: 'Remove access',
		description:
			'The text is shown on the primary confirm button in the bulk access confirmation dialog when the admin is removing a principal from all spaces.',
	},
});

const ConfirmatonSubSection = xcss({
	paddingTop: 'space.300',
});

const boldString = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

export const ChangeConfirmationDialog = ({
	principalName,
	isRemoval,
	spaceSelection,
	roleName,
	onConfirm,
	onCancel,
}: {
	principalName: string;
	isRemoval: boolean;
	spaceSelection: SpaceSelection;
	roleName?: string;
	onConfirm: () => void;
	onCancel: () => void;
}) => {
	const getConfirmationDialogDescription = () => {
		if (isRemoval) {
			switch (spaceSelection) {
				case SpaceSelection.AllExcludingPersonal:
					return i18n.removeAllExcludingPersonalConfirmationDialogDescription;
				case SpaceSelection.AllIncludingPersonal:
					return i18n.removeAllConfirmationDialogDescription;
				case SpaceSelection.OnlyPersonal:
					return i18n.removeAllPersonalConfirmationDialogDescription;
			}
		} else {
			switch (spaceSelection) {
				case SpaceSelection.AllExcludingPersonal:
					return i18n.addAllExcludingPersonalConfirmationDialogDescription;
				case SpaceSelection.AllIncludingPersonal:
					return i18n.addAllConfirmationDialogDescription;
				case SpaceSelection.OnlyPersonal:
					return i18n.addAllPersonalConfirmationDialogDescription;
			}
		}
	};

	return (
		<>
			<ModalHeader hasCloseButton>
				<ScreenEvent
					name="bulk-access-dialog"
					id="bulk-access-dialog"
					attributes={{ action: isRemoval ? 'remove' : 'add', spaceType: spaceSelection }}
				/>
				<ModalTitle appearance={isRemoval ? 'warning' : undefined}>
					<FormattedMessage
						{...(isRemoval ? i18n.removeConfirmationDialogTitle : i18n.addConfirmationDialogTitle)}
						values={{
							principalName,
						}}
					/>
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Box>
					<FormattedMessage
						{...getConfirmationDialogDescription()}
						values={{
							b: boldString,
							roleName,
						}}
					/>
					<Stack xcss={ConfirmatonSubSection}>
						<FormattedMessage
							{...i18n.confirmationDialogWarning}
							values={{
								b: boldString,
							}}
						/>
					</Stack>
				</Box>
			</ModalBody>
			<ModalFooter>
				<Button appearance="subtle" onClick={onCancel}>
					<FormattedMessage {...i18n.cancelButtonText} />
				</Button>
				<Button
					testId="confirmation-dialog-confirm-button"
					appearance={isRemoval ? 'warning' : 'primary'}
					onClick={onConfirm}
				>
					<FormattedMessage {...(isRemoval ? i18n.removeButtonText : i18n.addButtonText)} />
				</Button>
			</ModalFooter>
		</>
	);
};
